// ═══════════════════════════════════════════════════════════════════════
//   PLANOS POR TURMA — Motor Financeiro Central · FASE 2
//   Multi-categoria: 1 plano = N items (mensalidade + bilíngue + ...)
//   Aplicação em lote: gera títulos em db.titles com anti-duplicidade.
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit, Layers, Sparkles, Play, Loader2, X,
  CheckCircle2, AlertTriangle, Wallet, Eye, ArrowRightLeft, Wand2, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const DEFAULT_ITEM = {
  categoria: 'mensalidade',
  descricao: '',
  valor: 0,
  parcelas: 12,
  dia_vencimento: 10,
  mes_inicio: 1,
  desconto_pontualidade_pct: 0,
  multa_pct: 2.0,
  juros_dia_pct: 0.033,
};

const fmtBRL = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const CATEGORIA_BADGES = {
  mensalidade: 'bg-violet-50 text-violet-700 border-violet-200',
  bilingue: 'bg-blue-50 text-blue-700 border-blue-200',
  material: 'bg-amber-50 text-amber-700 border-amber-200',
  matricula: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rematricula: 'bg-teal-50 text-teal-700 border-teal-200',
  integral: 'bg-rose-50 text-rose-700 border-rose-200',
  extras: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  outros: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function PlanosTurma() {
  const [planos, setPlanos] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', class_group_id: '', year: new Date().getFullYear(), items: [{ ...DEFAULT_ITEM }], description: '' });
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateSrc, setMigrateSrc] = useState(null);
  const [migrateTarget, setMigrateTarget] = useState('');
  const [migrateReason, setMigrateReason] = useState('');
  const [migrating, setMigrating] = useState(false);
  // Build-from-titles
  const [buildOpen, setBuildOpen] = useState(false);
  const [buildForm, setBuildForm] = useState({ year: new Date().getFullYear(), class_group_id: '', dry_run: true });
  const [buildResult, setBuildResult] = useState(null);
  const [buildLoading, setBuildLoading] = useState(false);
  // Auto-gerados (filtro + ações)
  const [showOnlyAuto, setShowOnlyAuto] = useState(false);
  const [autoPlans, setAutoPlans] = useState([]);
  const [syncing, setSyncing] = useState({});

  const loadAutoPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/course-financial-plans?auto_source=build_from_titles`, { headers: auth() });
      const data = await res.json();
      setAutoPlans(Array.isArray(data) ? data : []);
    } catch { setAutoPlans([]); }
  };
  useEffect(() => { if (showOnlyAuto) loadAutoPlans(); /* eslint-disable-next-line */ }, [showOnlyAuto]);

  const handleSyncValues = async (planId, planName) => {
    setSyncing(s => ({ ...s, [planId]: true }));
    try {
      // Dry-run primeiro
      const r1 = await fetch(`${API_URL}/api/financial/plans/${planId}/sync-values?dry_run=true`, { method: 'POST', headers: auth() });
      const d1 = await r1.json();
      if (!r1.ok) throw new Error(d1.detail || 'Erro');
      if (!d1.changed) {
        toast.info(`"${planName}": valor já está sincronizado (R$ ${d1.current_value || 0})`);
        return;
      }
      const ok = window.confirm(`Atualizar valor de "${planName}" de R$ ${d1.current_value} para R$ ${d1.new_value}?\n(Baseado em ${d1.title_count} títulos vinculados — moda dos valores)\n\nNÃO altera títulos, apenas o valor padrão do plano.`);
      if (!ok) return;
      const r2 = await fetch(`${API_URL}/api/financial/plans/${planId}/sync-values?dry_run=false`, { method: 'POST', headers: auth() });
      const d2 = await r2.json();
      if (!r2.ok) throw new Error(d2.detail || 'Erro');
      toast.success(`Valor atualizado: R$ ${d2.current_value} → R$ ${d2.new_value}`);
      loadAutoPlans();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(s => ({ ...s, [planId]: false })); }
  };

  const handleRefazerBuild = (plan) => {
    // Pré-popula form de build com year+class_group inferidos
    setBuildForm({
      year: plan.auto_year || new Date().getFullYear(),
      class_group_id: plan.class_group_id || '',
      dry_run: true,
    });
    setBuildResult(null);
    setBuildOpen(true);
  };

  const handleBuildFromTitles = async (asDry) => {
    setBuildLoading(true);
    setBuildResult(null);
    try {
      const params = new URLSearchParams({ dry_run: asDry ? 'true' : 'false' });
      const body = { only_active_classes: true };
      if (buildForm.year) body.year = Number(buildForm.year);
      if (buildForm.class_group_id) body.class_group_id = buildForm.class_group_id;
      const res = await fetch(`${API_URL}/api/financial/plans/build-from-titles?${params.toString()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      setBuildResult({ ...data, was_dry: asDry });
      if (!asDry) {
        toast.success(`${data.summary?.created || 0} plano(s) criado(s), ${data.summary?.titles_linked || 0} título(s) vinculado(s)`);
        await load();
      }
    } catch (e) {
      toast.error(e.message || 'Erro');
    } finally {
      setBuildLoading(false);
    }
  };

  const token = () => localStorage.getItem('token');
  const auth = () => ({ Authorization: `Bearer ${token()}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cgRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/financial/planos-turma`, { headers: auth() }),
        fetch(`${API_URL}/api/class-groups`, { headers: auth() }),
        fetch(`${API_URL}/api/financial/planos-turma/categorias/lista`, { headers: auth() }),
      ]);
      if (pRes.ok) setPlanos(await pRes.json());
      if (cgRes.ok) {
        const cg = await cgRes.json();
        setClassGroups(Array.isArray(cg) ? cg : (cg.items || []));
      }
      if (catRes.ok) setCategorias(await catRes.json());
    } catch {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', class_group_id: '', year: new Date().getFullYear(), items: [{ ...DEFAULT_ITEM }], description: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      class_group_id: p.class_group_id || '',
      year: p.year || new Date().getFullYear(),
      items: (p.items?.length ? p.items : [{ ...DEFAULT_ITEM }]).map(i => ({ ...DEFAULT_ITEM, ...i })),
      description: p.description || '',
    });
    setDialogOpen(true);
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...DEFAULT_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));
  const updateItem = (idx, k, v) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  const valorTotalPlano = form.items.reduce((s, it) => s + (Number(it.valor) || 0) * (Number(it.parcelas) || 1), 0);

  const handleSave = async () => {
    if (!form.name?.trim()) return toast.error('Nome do plano é obrigatório');
    if (!form.class_group_id) return toast.error('Selecione a turma');
    if (form.items.some(it => !(Number(it.valor) > 0))) return toast.error('Todos os itens precisam ter valor > 0');
    setSaving(true);
    try {
      const url = editing
        ? `${API_URL}/api/financial/planos-turma/${editing.id}`
        : `${API_URL}/api/financial/planos-turma`;
      const method = editing ? 'PUT' : 'POST';
      const body = {
        name: form.name,
        class_group_id: form.class_group_id,
        year: Number(form.year),
        description: form.description,
        items: form.items.map(i => ({
          categoria: i.categoria,
          descricao: i.descricao || null,
          valor: Number(i.valor),
          parcelas: Number(i.parcelas) || 1,
          dia_vencimento: Number(i.dia_vencimento) || 10,
          mes_inicio: Number(i.mes_inicio) || 1,
          desconto_pontualidade_pct: Number(i.desconto_pontualidade_pct) || 0,
          multa_pct: Number(i.multa_pct) || 0,
          juros_dia_pct: Number(i.juros_dia_pct) || 0,
        })),
      };
      const res = await fetch(url, { method, headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao salvar');
      }
      toast.success(editing ? 'Plano atualizado' : 'Plano criado');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Excluir o plano "${p.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/financial/planos-turma/${p.id}`, { method: 'DELETE', headers: auth() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao excluir');
      }
      toast.success('Plano excluído');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handlePreview = async (p) => {
    try {
      const res = await fetch(`${API_URL}/api/financial/planos-turma/${p.id}/preview`, { headers: auth() });
      if (!res.ok) throw new Error('Erro no preview');
      const data = await res.json();
      setPreview({ ...data, plano: p });
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleApply = async (overwrite = false) => {    if (!preview?.plano) return;
    setApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/financial/planos-turma/${preview.plano.id}/aplicar`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ overwrite }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao aplicar');
      }
      const result = await res.json();
      toast.success(`✓ ${result.criados} título(s) gerado(s) · ${result.ignorados_duplicados} duplicado(s) ignorado(s)`);
      setPreviewOpen(false);
      setPreview(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplying(false);
    }
  };

  const openMigrate = (p) => {
    setMigrateSrc(p);
    setMigrateTarget('');
    setMigrateReason('');
    setMigrateOpen(true);
  };

  const handleMigrate = async () => {
    if (!migrateSrc) return;
    if (!migrateTarget) return toast.error('Selecione o plano de destino');
    setMigrating(true);
    try {
      const res = await fetch(`${API_URL}/api/financial/plano-temporario/${migrateSrc.id}/migrar`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_plan_id: migrateTarget, reason: migrateReason || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao migrar');
      }
      const result = await res.json();
      toast.success(`✓ ${result.moved} título(s) migrado(s)`);
      setMigrateOpen(false);
      setMigrateSrc(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMigrating(false);
    }
  };

  // ─── Excluir títulos dos alunos gerados por este plano ──────────────────
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePlan, setPurgePlan] = useState(null);
  const [purgeMode, setPurgeMode] = useState('all'); // 'all' | 'student'
  const [purgeStudents, setPurgeStudents] = useState([]);
  const [purgeStudentId, setPurgeStudentId] = useState('');
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeLoadingStudents, setPurgeLoadingStudents] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);

  const openPurge = async (p) => {
    setPurgePlan(p);
    setPurgeMode('all');
    setPurgeStudentId('');
    setPurgeReason('');
    setPurgePreview(null);
    setPurgeOpen(true);
    setPurgeStudents([]);
    if (p.class_group_id) {
      setPurgeLoadingStudents(true);
      try {
        const res = await fetch(`${API_URL}/api/students?class_group_id=${p.class_group_id}&limit=2000`, { headers: auth() });
        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data) ? data : (data.items || []);
          setPurgeStudents(arr.map(s => ({ id: s.id, name: s.name || s.full_name })));
        }
      } catch { /* noop */ }
      finally { setPurgeLoadingStudents(false); }
    }
  };

  const closePurge = () => {
    if (purgeBusy) return;
    setPurgeOpen(false);
    setPurgePlan(null);
    setPurgePreview(null);
  };

  const buildPurgePayload = () => {
    if (!purgePlan) return null;
    const filters = { plan_id: purgePlan.id };
    const payload = { filters, reason: purgeReason || null };
    if (purgeMode === 'student') {
      if (!purgeStudentId) return null;
      payload.student_id = purgeStudentId;
    }
    return payload;
  };

  const runPurge = async (asDry) => {
    const payload = buildPurgePayload();
    if (!payload) return toast.error(purgeMode === 'student' ? 'Selecione o aluno' : 'Plano inválido');
    if (!asDry && !(purgeReason || '').trim()) return toast.error('Motivo obrigatório para excluir títulos');
    setPurgeBusy(true);
    try {
      const url = `${API_URL}/api/financial/titles/delete-bulk?dry_run=${asDry ? 'true' : 'false'}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Erro');
      if (asDry) {
        setPurgePreview(data);
      } else {
        toast.success(`${data.deleted || 0} título(s) excluído(s)${data.reverted_payments ? ` · ${data.reverted_payments} pagto(s) revertido(s)` : ''}`);
        setPurgeOpen(false);
        setPurgePlan(null);
        setPurgePreview(null);
      }
    } catch (e) {
      toast.error(e.message || 'Erro');
    } finally {
      setPurgeBusy(false);
    }
  };

  // ─── Render ───
  return (
    <div className="space-y-5 p-6" data-testid="planos-turma-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Layers className="w-6 h-6 text-violet-600" />
            Planos Financeiros por Turma
          </h1>
          <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1">
            <Sparkles className="w-3.5 h-3.5 text-violet-500" />
            FASE 2 · Motor Financeiro Central · multi-categoria com aplicação em lote
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowOnlyAuto(s => !s)}
            variant={showOnlyAuto ? 'default' : 'outline'}
            data-testid="btn-toggle-auto-plans"
            className={showOnlyAuto ? 'bg-violet-600 hover:bg-violet-700' : 'border-violet-200 text-violet-700 hover:bg-violet-50'}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {showOnlyAuto ? `Auto-gerados (${autoPlans.length})` : 'Ver auto-gerados'}
          </Button>
          <Button
            onClick={() => { setBuildOpen(true); setBuildResult(null); }}
            variant="outline"
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="btn-build-from-titles"
          >
            <Wand2 className="w-4 h-4 mr-2" /> Gerar planos a partir de títulos
          </Button>
          <Button onClick={openNew} className="bg-violet-600 hover:bg-violet-700" data-testid="btn-novo-plano">
            <Plus className="w-4 h-4 mr-2" /> Novo Plano
          </Button>
        </div>
      </div>

      {/* Tabela de auto-gerados (build_from_titles) */}
      {showOnlyAuto && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/30 p-4 mb-4" data-testid="auto-plans-table">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-bold text-violet-900 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" /> Planos auto-gerados a partir de títulos importados
              </p>
              <p className="text-[11px] text-violet-700 mt-0.5">{autoPlans.length} plano(s). Use "Sincronizar valores" para atualizar a moda quando títulos novos chegarem.</p>
            </div>
          </div>
          {autoPlans.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-3 text-center">Nenhum plano auto-gerado ainda. Use "Gerar planos a partir de títulos".</p>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg border border-violet-100">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase">
                  <tr>
                    <th className="text-left px-2 py-1.5">Nome</th>
                    <th className="text-left px-2 py-1.5">Categoria</th>
                    <th className="text-center px-2 py-1.5">Ano</th>
                    <th className="text-right px-2 py-1.5">Valor (moda)</th>
                    <th className="text-right px-2 py-1.5">Parcelas</th>
                    <th className="text-center px-2 py-1.5">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {autoPlans.map(p => (
                    <tr key={p.id} className="border-t" data-testid={`auto-plan-row-${p.id}`}>
                      <td className="px-2 py-1.5 font-medium">{p.name}</td>
                      <td className="px-2 py-1.5">
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{p.category}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">{p.auto_year || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(p.installment_value || 0)}</td>
                      <td className="px-2 py-1.5 text-right">{p.num_installments}x</td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleSyncValues(p.id, p.name)} disabled={syncing[p.id]} className="h-7 px-2 text-[10px] text-emerald-700 hover:bg-emerald-50" data-testid={`btn-sync-values-${p.id}`}>
                            {syncing[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightLeft className="w-3 h-3 mr-1" />}
                            Sincronizar
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleRefazerBuild(p)} className="h-7 px-2 text-[10px] text-violet-700 hover:bg-violet-50" data-testid={`btn-rebuild-${p.id}`}>
                            <Wand2 className="w-3 h-3 mr-1" /> Refazer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="rounded-xl border border-slate-200 p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
        </div>
      ) : planos.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-16 text-center">
          <Layers className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nenhum plano cadastrado.</p>
          <Button onClick={openNew} variant="outline" className="mt-4">Criar primeiro plano</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {planos.map(p => {
            const total = (p.items || []).reduce((s, it) => s + (it.valor || 0) * (it.parcelas || 1), 0);
            return (
              <div key={p.id} className="rounded-2xl border border-slate-100 bg-white p-4 hover:border-violet-300 hover:shadow-md transition-all space-y-3" data-testid={`plano-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-800 truncate">{p.name}</p>
                      {p.is_temporary && !p.migrated_to && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded" data-testid={`badge-temp-${p.id}`}>
                          <AlertTriangle className="w-2.5 h-2.5" />
                          Temporário
                        </span>
                      )}
                      {p.migrated_to && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
                          Migrado
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">{p.class_group_name || '-'} · {p.year}</p>
                  </div>
                  <div className="flex gap-1">
                    {p.is_temporary && !p.migrated_to && (
                      <button onClick={() => openMigrate(p)} title="Migrar para plano definitivo"
                        className="p-1.5 rounded-lg hover:bg-amber-50"
                        data-testid={`btn-migrate-${p.id}`}>
                        <ArrowRightLeft className="w-4 h-4 text-amber-600" />
                      </button>
                    )}
                    <button onClick={() => handlePreview(p)} title="Preview / Aplicar" className="p-1.5 rounded-lg hover:bg-violet-50" data-testid={`btn-preview-${p.id}`}>
                      <Play className="w-4 h-4 text-violet-600" />
                    </button>
                    <button onClick={() => openPurge(p)} title="Excluir títulos dos alunos (de uma vez ou por aluno)" className="p-1.5 rounded-lg hover:bg-amber-50" data-testid={`btn-purge-${p.id}`}>
                      <Wallet className="w-4 h-4 text-amber-600" />
                    </button>
                    <button onClick={() => openEdit(p)} title="Editar" className="p-1.5 rounded-lg hover:bg-slate-100">
                      <Edit className="w-4 h-4 text-slate-500" />
                    </button>
                    <button onClick={() => handleDelete(p)} title="Excluir" className="p-1.5 rounded-lg hover:bg-rose-50">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {(p.items || []).map((it, idx) => (
                    <span key={idx} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${CATEGORIA_BADGES[it.categoria] || CATEGORIA_BADGES.outros}`}>
                      {it.categoria} · {it.parcelas}x {fmtBRL(it.valor)}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <span className="text-[11px] text-slate-500">Total/aluno</span>
                  <span className="text-base font-bold text-violet-700">{fmtBRL(total)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Dialog Criar/Editar ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Plano' : 'Novo Plano por Turma'}</DialogTitle>
            <DialogDescription>Defina N categorias (mensalidade, bilíngue, material, etc.) com valor, parcelas e vencimento.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Nome do plano *</Label>
                <Input data-testid="plan-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex.: 6º ANO A 2026" />
              </div>
              <div>
                <Label className="text-xs">Ano</Label>
                <Input type="number" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Turma *</Label>
                <Select value={form.class_group_id} onValueChange={v => setForm(f => ({ ...f, class_group_id: v }))}>
                  <SelectTrigger data-testid="plan-class-group"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
                  <SelectContent>
                    {classGroups.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Itens do plano</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="btn-add-item">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar item
                </Button>
              </div>
              <div className="space-y-3">
                {form.items.map((it, idx) => (
                  <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 relative" data-testid={`item-${idx}`}>
                    {form.items.length > 1 && (
                      <button onClick={() => removeItem(idx)} className="absolute top-2 right-2 p-1 rounded hover:bg-rose-100" title="Remover">
                        <X className="w-3.5 h-3.5 text-rose-500" />
                      </button>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div className="col-span-2 md:col-span-2">
                        <Label className="text-[11px]">Categoria</Label>
                        <Select value={it.categoria} onValueChange={v => updateItem(idx, 'categoria', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px]">Valor (R$)</Label>
                        <Input type="number" step="0.01" value={it.valor} onChange={e => updateItem(idx, 'valor', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Parcelas</Label>
                        <Input type="number" min="1" max="24" value={it.parcelas} onChange={e => updateItem(idx, 'parcelas', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Mês início</Label>
                        <Input type="number" min="1" max="12" value={it.mes_inicio} onChange={e => updateItem(idx, 'mes_inicio', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Dia venc.</Label>
                        <Input type="number" min="1" max="28" value={it.dia_vencimento} onChange={e => updateItem(idx, 'dia_vencimento', e.target.value)} />
                      </div>
                      <div className="col-span-2 md:col-span-2">
                        <Label className="text-[11px]">Descrição (opcional)</Label>
                        <Input value={it.descricao || ''} onChange={e => updateItem(idx, 'descricao', e.target.value)} placeholder={it.categoria} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Desc. pontualidade %</Label>
                        <Input type="number" step="0.1" value={it.desconto_pontualidade_pct} onChange={e => updateItem(idx, 'desconto_pontualidade_pct', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Multa %</Label>
                        <Input type="number" step="0.1" value={it.multa_pct} onChange={e => updateItem(idx, 'multa_pct', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Juros dia %</Label>
                        <Input type="number" step="0.001" value={it.juros_dia_pct} onChange={e => updateItem(idx, 'juros_dia_pct', e.target.value)} />
                      </div>
                    </div>
                    <div className="text-right text-[11px] text-slate-500 mt-1">
                      Subtotal: <span className="font-semibold text-slate-700">{fmtBRL((Number(it.valor) || 0) * (Number(it.parcelas) || 1))}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between bg-violet-50 rounded-xl p-3 border border-violet-100">
              <span className="text-sm font-semibold text-violet-700">Total por aluno</span>
              <span className="text-xl font-black text-violet-700">{fmtBRL(valorTotalPlano)}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="btn-save-plan">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar alterações' : 'Criar plano')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog Preview / Aplicar ─── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-violet-600" /> Preview · {preview?.plano?.name}
            </DialogTitle>
            <DialogDescription>
              Ao aplicar, os títulos serão gravados em <code className="text-violet-600">db.titles</code>.
              Anti-duplicidade automática via <code className="text-violet-600">external_id</code>.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-violet-50 border border-violet-100 p-3">
                  <p className="text-[10px] uppercase font-semibold text-violet-500">Alunos</p>
                  <p className="text-2xl font-bold text-violet-700">{preview.alunos}</p>
                </div>
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-3">
                  <p className="text-[10px] uppercase font-semibold text-blue-500">Títulos</p>
                  <p className="text-2xl font-bold text-blue-700">{preview.titulos_a_criar}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                  <p className="text-[10px] uppercase font-semibold text-emerald-500">Valor total</p>
                  <p className="text-base font-bold text-emerald-700">{fmtBRL(preview.valor_total)}</p>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-[11px] uppercase font-semibold text-slate-500 mb-2">Desdobramento</p>
                <ul className="space-y-1">
                  {(preview.items || []).map((it, idx) => (
                    <li key={idx} className="flex items-center justify-between text-sm py-1">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${CATEGORIA_BADGES[it.categoria] || CATEGORIA_BADGES.outros}`}>
                        {it.label}
                      </span>
                      <span className="text-slate-600">{it.titulos_total} títulos · {fmtBRL(it.valor_total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {preview.alunos === 0 && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Esta turma não tem alunos ativos. Nenhum título será gerado.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={applying}>Cancelar</Button>
            <Button
              onClick={() => handleApply(false)}
              disabled={applying || !preview?.alunos}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="btn-apply-plan"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Aplicar à turma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog Migrar para plano definitivo ─── */}
      <Dialog open={migrateOpen} onOpenChange={setMigrateOpen}>
        <DialogContent className="max-w-md" data-testid="migrate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" /> Migrar para plano definitivo
            </DialogTitle>
            <DialogDescription>
              Origem: <span className="font-semibold text-slate-700">{migrateSrc?.name}</span>.
              Apenas <code className="text-violet-600">plan_id</code> dos títulos será alterado — valores, status, vencimentos e vínculos permanecem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Plano de destino *</Label>
              <Select value={migrateTarget} onValueChange={setMigrateTarget}>
                <SelectTrigger data-testid="migrate-target-select"><SelectValue placeholder="Selecione um plano definitivo" /></SelectTrigger>
                <SelectContent>
                  {planos.filter(x => !x.is_temporary && !x.migrated_to && x.id !== migrateSrc?.id).map(x => (
                    <SelectItem key={x.id} value={x.id}>
                      {x.name} {x.class_group_name ? `· ${x.class_group_name}` : ''} · {x.year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {planos.filter(x => !x.is_temporary && !x.migrated_to).length === 0 && (
                <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Crie um plano definitivo primeiro.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Motivo (opcional)</Label>
              <Input value={migrateReason} onChange={e => setMigrateReason(e.target.value)} placeholder="Ex.: Plano definitivo da turma criado em ..." data-testid="migrate-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrateOpen(false)} disabled={migrating}>Cancelar</Button>
            <Button onClick={handleMigrate} disabled={migrating || !migrateTarget} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-migrate-confirm">
              {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar migração'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Build-from-Titles */}
      <Dialog open={buildOpen} onOpenChange={(o) => { if (!buildLoading) setBuildOpen(o); }}>
        <DialogContent className="max-w-2xl" data-testid="build-titles-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Wand2 className="w-5 h-5" /> Gerar planos a partir de títulos
            </DialogTitle>
            <DialogDescription>
              Lê <strong>db.titles</strong> e cria/atualiza planos por turma + categoria. Idempotente. Sem alterar valores reais, status, pagamentos ou boletos.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Ano letivo</Label>
              <Input type="number" value={buildForm.year} onChange={e => setBuildForm(f => ({ ...f, year: e.target.value }))} data-testid="build-year" />
            </div>
            <div>
              <Label className="text-xs">Turma (opcional)</Label>
              <Select value={buildForm.class_group_id || '__all__'} onValueChange={v => setBuildForm(f => ({ ...f, class_group_id: v === '__all__' ? '' : v }))}>
                <SelectTrigger data-testid="build-class-group"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas as turmas ativas</SelectItem>
                  {classGroups.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name || cg.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {buildLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Processando…</div>
          )}

          {buildResult && (
            <div className="mt-3 space-y-3">
              <div className={`rounded-lg border px-3 py-2 ${buildResult.was_dry ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <p className={`text-sm font-bold ${buildResult.was_dry ? 'text-amber-900' : 'text-emerald-900'}`}>
                  {buildResult.was_dry ? '🧪 Prévia (dry-run)' : '✅ Aplicado'} ·
                  {' '}{buildResult.summary?.created || 0} plano(s) {buildResult.was_dry ? 'a serem criados' : 'criados'},
                  {' '}{buildResult.summary?.students_linked || 0} aluno(s),
                  {' '}{buildResult.summary?.titles_linked || 0} título(s)
                </p>
                {/* Telemetria de resolução de turma */}
                {buildResult.summary?.titles_total > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                    <span className="text-slate-700"><strong>{buildResult.summary?.titles_total}</strong> total</span>
                    {buildResult.summary?.titles_com_turma_direta > 0 && <span className="text-emerald-800">{buildResult.summary.titles_com_turma_direta} c/ turma direta</span>}
                    {buildResult.summary?.titles_com_turma_por_aluno > 0 && <span className="text-blue-800">{buildResult.summary.titles_com_turma_por_aluno} resolvidos via aluno</span>}
                    {buildResult.summary?.titles_sem_turma > 0 && <span className="text-rose-700">{buildResult.summary.titles_sem_turma} sem turma</span>}
                  </div>
                )}
                {(buildResult.summary?.skipped_no_class > 0 || buildResult.summary?.skipped_no_category > 0 || buildResult.summary?.skipped_no_year > 0) && (
                  <p className="text-[11px] text-slate-700 mt-1">
                    Pulados: {buildResult.summary?.skipped_no_class || 0} sem turma, {buildResult.summary?.skipped_no_category || 0} sem categoria, {buildResult.summary?.skipped_no_year || 0} sem ano
                  </p>
                )}
              </div>
              {(buildResult.groups || []).length > 0 && (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase">
                      <tr>
                        <th className="text-left px-2 py-1.5">Turma</th>
                        <th className="text-left px-2 py-1.5">Categoria</th>
                        <th className="text-right px-2 py-1.5">Valor (moda)</th>
                        <th className="text-right px-2 py-1.5">Alunos</th>
                        <th className="text-right px-2 py-1.5">Títulos</th>
                        <th className="text-center px-2 py-1.5">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buildResult.groups.map((g, i) => (
                        <tr key={i} className="border-t" data-testid={`build-row-${i}`}>
                          <td className="px-2 py-1.5">{g.class_name}</td>
                          <td className="px-2 py-1.5">{g.category_label}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(g.plan_value_mode || 0)}</td>
                          <td className="px-2 py-1.5 text-right">{g.students_linked}/{g.students_count}</td>
                          <td className="px-2 py-1.5 text-right">{g.titles_linked}/{g.titles_count}</td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${g.action === 'created' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                              {g.action === 'created' ? 'novo' : 'vinculado'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(buildResult.summary?.conflicts || []).length > 0 && (
                <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-900">
                  <strong>{buildResult.summary.conflicts.length}</strong> título(s) já vinculado(s) a OUTRO plano e foram preservados (não alterados).
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBuildOpen(false)} disabled={buildLoading}>Fechar</Button>
            <Button variant="outline" onClick={() => handleBuildFromTitles(true)} disabled={buildLoading} data-testid="btn-build-dryrun">
              <Eye className="w-4 h-4 mr-2" /> Prévia
            </Button>
            <Button
              onClick={() => handleBuildFromTitles(false)}
              disabled={buildLoading || !buildResult || buildResult.was_dry === false}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="btn-build-apply"
            >
              {buildLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog: Excluir títulos do plano (todos os alunos ou por aluno) ─── */}
      <Dialog open={purgeOpen} onOpenChange={(o) => !o && closePurge()}>
        <DialogContent className="max-w-lg" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle>Excluir títulos dos alunos</DialogTitle>
            <DialogDescription>
              Plano: <strong>{purgePlan?.name}</strong> — {purgePlan?.class_group_name || '—'} · {purgePlan?.year}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-900 flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Exclusão financeira <strong>SEGURA</strong> (soft delete): os títulos são marcados como excluídos e
                  saem do DRE/Caixa/Dashboards, mas <em>não são apagados fisicamente</em>. É possível restaurar
                  depois em Financeiro → Títulos. Pagamentos efetuados são revertidos automaticamente.
                </span>
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Escopo</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => { setPurgeMode('all'); setPurgePreview(null); }}
                  className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${purgeMode === 'all' ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white hover:border-rose-200'}`}
                  data-testid="purge-mode-all"
                >
                  <p className="text-sm font-bold text-slate-800">Todos os alunos</p>
                  <p className="text-[11px] text-slate-500">Toda a turma vinculada a este plano</p>
                </button>
                <button
                  type="button"
                  onClick={() => { setPurgeMode('student'); setPurgePreview(null); }}
                  className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${purgeMode === 'student' ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white hover:border-rose-200'}`}
                  data-testid="purge-mode-student"
                >
                  <p className="text-sm font-bold text-slate-800">Por aluno</p>
                  <p className="text-[11px] text-slate-500">Selecionar 1 aluno da turma</p>
                </button>
              </div>
            </div>

            {purgeMode === 'student' && (
              <div>
                <Label className="text-xs">Aluno *</Label>
                <Select value={purgeStudentId} onValueChange={(v) => { setPurgeStudentId(v); setPurgePreview(null); }}>
                  <SelectTrigger data-testid="purge-student-select">
                    <SelectValue placeholder={purgeLoadingStudents ? 'Carregando...' : 'Selecione o aluno'} />
                  </SelectTrigger>
                  <SelectContent>
                    {purgeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!purgeLoadingStudents && purgeStudents.length === 0 && (
                  <p className="text-[11px] text-slate-500 mt-1">Nenhum aluno encontrado nesta turma.</p>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs">Motivo *</Label>
              <Input
                value={purgeReason}
                onChange={e => setPurgeReason(e.target.value)}
                placeholder="Ex.: Reaplicação do plano com valores corrigidos"
                data-testid="purge-reason"
              />
            </div>

            {purgePreview && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-xs">
                <p className="font-bold text-rose-900 mb-1">Pré-visualização</p>
                <p>Títulos a excluir: <strong>{purgePreview.would_delete ?? purgePreview.counts?.total ?? 0}</strong></p>
                {purgePreview.counts && (
                  <p className="text-rose-700 mt-1">
                    Pagos: {purgePreview.counts.paid || 0} · Em aberto: {purgePreview.counts.open || 0} · Vencidos: {purgePreview.counts.overdue || 0}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closePurge} disabled={purgeBusy}>Cancelar</Button>
            <Button
              variant="outline"
              onClick={() => runPurge(true)}
              disabled={purgeBusy || (purgeMode === 'student' && !purgeStudentId)}
              data-testid="purge-preview-btn"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              {purgeBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Pré-visualizar
            </Button>
            <Button
              onClick={() => runPurge(false)}
              disabled={purgeBusy || !(purgeReason || '').trim() || (purgeMode === 'student' && !purgeStudentId)}
              data-testid="purge-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {purgeBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Excluir títulos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
